import {Subscription} from 'rxjs';
import {isNullOrUndefined} from 'util';
import {AbstractControl, AsyncValidatorFn, FormArray, FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {isNil, isFunction} from 'lodash';
import {matchValidatorFactory} from '../components/validation/validators';

export type ControlDefinition = {
    value?: any;
    disabled?: boolean;
    validation?: {
        required?: boolean;
        requiredTrue?: boolean;
        minlength?: number;
        maxlength?: number;
        match?: string;
        pattern?: string | RegExp;
        nullValidator?: boolean;
        [key: string]: any | ValidatorFn;
    },
    validationAsync?: {
        [key: string]: AsyncValidatorFn;
    }
};

export type SetOfControlDefinition ={
    [key: string]: ControlDefinition;
}

export class FormDefinitionControl {

    private form: FormGroup;
    private controls: string[] = [];
    private validation: ValidatorFn;

    public unRegister() {
        FormUtils.unRegister(this.form, this.controls, this.validation);
    }

    setFormGroup(form: FormGroup) {
        this.form = form;
    }

    getFormGroup() : FormGroup {
        return this.form
    }

    putControlName(control: string) {
        this.controls.push(control);
    }

    setFormGroupValidation(validator: ValidatorFn) {
        this.validation = validator;
    }
}

export class FormUtils {

    public static updateValidity(form: FormGroup, control: string, timeout?: boolean) {
        if (isNil(timeout)) {
            timeout = true;
        }
        const updateFun = () => form.controls[control].updateValueAndValidity({onlySelf: true});
        if (timeout) {
            setTimeout(updateFun);
        } else {
            updateFun();
        }
    }

        public static markAsTouched(form: FormGroup|FormArray) {
            if (form) {
                for (let control in form.controls) {
                    let c = form.controls[control];

                    if (c instanceof FormControl) {
                        c.markAsTouched( {onlySelf: true} );
                    } else {
                        FormUtils.markAsTouched(c);
                    }
                }
            }
        }

    public static isValidControl(control: AbstractControl): boolean {
        return control && control.valid;
    }

    public static isValidControls(controls: AbstractControl[]): boolean {
        for (let control of controls) {
            if (!FormUtils.isValidControl(control)) {
                return false;
            }
        }

        return true;
    }

    public static isValid(form: FormGroup, considerFormItself: boolean = false): boolean {
        let valid: boolean = true;
        if (form) {
            for (let control in form.controls) {
                form.controls[control].markAsTouched({onlySelf: true});
                if (!form.controls[control].valid && form.controls[control].enabled) {
                    valid = false;
                }
            }
        }
        console.log('is Valid form: ' + valid && (considerFormItself ? form.valid : true));
        return valid && (considerFormItself ? form.valid : true);
    }

    public static register(form: FormGroup, controls: SetOfControlDefinition, validator?: ValidatorFn): FormDefinitionControl {
        const definition: FormDefinitionControl = new FormDefinitionControl;
        definition.setFormGroup(form);

        for (let control in controls) {
            definition.putControlName(control);
            const config: any = controls[control] || {};

            form.registerControl(control, new FormControl(
                FormUtils.toFormState(config),
                FormUtils.toFormValidator(config),
                FormUtils.toFormAsyncValidator(config)
            ));
        }

        definition.setFormGroupValidation(form.validator);
        form.setValidators(Validators.compose([form.validator, validator]));

        return definition;
    }

    static unRegister(form: FormGroup, controls: string[], validator: ValidatorFn) {
        form.setValidators(validator);
        for (let control of controls) {
            form.removeControl(control);
        }
    }

    public static validateIf(checkFn: (control: AbstractControl) => boolean, validators: any[]) {
        return (control: AbstractControl) => {
            if (checkFn(control)) return Validators.compose(validators)
        }
    }


    public static disableEnableControls(form:FormGroup, targetControlName: string, enableDisableControlNames: string[]): Subscription {
        const enableDisableControls: AbstractControl[] = enableDisableControlNames.map(name => form.get(name));
        const targetControl: AbstractControl = form.get(targetControlName);
        const subscription = targetControl.valueChanges.subscribe(val => {
            enableDisableControls.map(control => {
                val ? control.enable() : control.disable();
            });
        });
        targetControl.updateValueAndValidity();
        return subscription;
    }

    private static toFormState(config: ControlDefinition) {
        return {
            value: config.value,
            disabled: config.disabled || false
        }
    }

    private static toFormValidator(config: ControlDefinition): ValidatorFn | ValidatorFn[] {
        const validators: ValidatorFn[] = [];

        for (let validator in config.validation) {
            const validatorFn: ValidatorFn = FormUtils.getFormValidator(validator, config.validation[validator]);
            if (!isNullOrUndefined(validatorFn)) validators.push(validatorFn);
        }

        return Validators.compose(validators);
    }

    private static getFormValidator(name: string, value: any): ValidatorFn {
        switch (name) {
            case 'required':
                return value && Validators.required;
            case 'requiredTrue':
                return value && Validators.requiredTrue;
            case 'minlength':
                return isFunction(value) ? value : Validators.minLength(Number(value));
            case 'maxlength':
                return isFunction(value) ? value : Validators.maxLength(Number(value));
            case 'pattern':
                return Validators.pattern(value);
            case 'match':
                return matchValidatorFactory(value);
            case 'nullValidator':
                return value && Validators.nullValidator;
            default:
                if (isFunction(value)) {
                    return value;
                }
        }
    }

    private static toFormAsyncValidator(config: ControlDefinition): AsyncValidatorFn | AsyncValidatorFn[] {
        const validators: AsyncValidatorFn[] = [];

        if (config.validationAsync) {
            for (let validator in config.validationAsync) {
                if (!isNullOrUndefined(config.validationAsync[validator])) validators.push(config.validationAsync[validator]);
            }
        }

        return Validators.composeAsync(validators);
    }

    public static configItemMapAction(src: any, config: {[kValue: string]: {enable: boolean}}, action: (kValue) => any) {
        Object.keys(src)
            .map(k => {
                let val = src[k];
                return {
                    val: val,
                    enable: isNullOrUndefined(config[val]) || config[val].enable
                }
            })
            .filter(kv => kv.enable)
            .forEach(kv => action(kv.val))
    }

}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./src/common/utils/form.utils.ts